import { reportError } from '@gef-ui/error-handler/actions';
import { getAccessToken } from '@gef-ui/redux-common/selectors/appContext';
import mergeDeepRight from "ramda/es/mergeDeepRight";
import o from "ramda/es/o";
import prop from "ramda/es/prop";
import isNotNil from "ramda-extension/es/isNotNil";
import { dissocOnError, getOnError } from '../../../auth/utils';
import { AUTH_SCHEMA_BEARER, CALL_API, OAUTH_HEADER_NAME } from '../../../constants';
import { setRequestStatus } from '../../../redux/status/actions';
import { requestStates } from '../../../redux/status/constants';
import { registerOauth2Middleware } from '../../globalConfiguration';
import { callActionsWithArgs, logApiInfoError, logHeaderEnhancerDebug, resolveValue, skipForMiddleware } from '../../utils';
import { MIDDLEWARE_NAME } from '../constants';
import { obtainTokensWithBuffering } from '../obtainTokensWithBuffering';
const headersP = prop('headers');
/**
 * oauth2 middleware
 *
 * @documented
 * @category api-middleware
 *
 * @param {Object} [obtainTokensOptions] - body params to be passed upon token get/refresh
 */ const oauth2Middleware = (obtainTokensOptions = {}, { getRequestToken = async ({ callAPI, state, next, getState })=>{
    if (!getAccessToken(state)) {
        await obtainTokensWithBuffering(callAPI, obtainTokensOptions)(next, getState);
    }
    return getAccessToken(state);
}, getUnauthToken = async ({ callAPI, next, getState })=>{
    await obtainTokensWithBuffering(callAPI, obtainTokensOptions)(next, getState);
} } = {}, maxRetries = 10)=>{
    registerOauth2Middleware();
    return ({ getState, dispatch })=>(next)=>async (action)=>{
                const callAPI = action[CALL_API];
                if (!callAPI || skipForMiddleware(MIDDLEWARE_NAME, callAPI)) {
                    return next(action);
                }
                const state = getState();
                const token = await getRequestToken({
                    callAPI,
                    state,
                    next,
                    getState
                });
                const resolveHeaderValueWithState = o(resolveValue(state), headersP);
                const oauthAuthorizationHeader = {
                    [OAUTH_HEADER_NAME]: `${AUTH_SCHEMA_BEARER} ${token}`
                };
                logHeaderEnhancerDebug(MIDDLEWARE_NAME, oauthAuthorizationHeader);
                const enhancedOauthHeaders = await resolveHeaderValueWithState(callAPI);
                const oauth2CallAPI = {
                    ...dissocOnError(callAPI),
                    headers: mergeDeepRight(enhancedOauthHeaders, oauthAuthorizationHeader),
                    notReportError: true,
                    originalNotReportError: callAPI.notReportError
                };
                try {
                    return await next({
                        [CALL_API]: oauth2CallAPI
                    });
                } catch (err) {
                    if (err.status === 401) {
                        logApiInfoError(err, MIDDLEWARE_NAME);
                        const requestUuid = callAPI?.meta?.requestUuid;
                        if (isNotNil(requestUuid)) {
                            dispatch(setRequestStatus({
                                requestUuid,
                                status: requestStates.IN_PROGRESS
                            }));
                        }
                        dispatch(setRequestStatus({
                            requestUuid,
                            status: requestStates.IN_QUEUE
                        }));
                        await getUnauthToken({
                            callAPI,
                            next,
                            getState,
                            token
                        });
                        if (!callAPI.meta) {
                            callAPI.meta = {};
                        }
                        callAPI.meta.unauthRetries = (callAPI.meta.unauthRetries || 0) + 1;
                        if (callAPI.meta.unauthRetries > maxRetries) {
                            if (!callAPI.notReportError) {
                                dispatch(reportError(err));
                            }
                            callActionsWithArgs(dispatch, callAPI.runInBatch, getOnError(callAPI), [
                                {
                                    failure: err
                                }
                            ]);
                            return Promise.reject(err);
                        }
                        return dispatch(action);
                    } else {
                        if (!callAPI.notReportError) {
                            dispatch(reportError(err));
                        }
                        callActionsWithArgs(dispatch, callAPI.runInBatch, getOnError(callAPI), [
                            {
                                failure: err
                            }
                        ]);
                        return Promise.reject(err);
                    }
                }
            };
};
export const oauth2MiddlewareAuthV2 = (obtainTokensOptions, authClient, maxRetries)=>oauth2Middleware(obtainTokensOptions, {
        getRequestToken: ()=>authClient.getAccessToken(),
        getUnauthToken: ({ token })=>authClient.getAccessToken({
                possiblyRevokedToken: token
            })
    }, maxRetries);
export default oauth2Middleware;
